import apiClient from '../apiClient';

/**
 * EPISODES — the thread a patient is in the middle of.
 *
 * Not a visit and not a diagnosis: a named clinical thread that spans visits,
 * carrying a state and the reason it is still open. See the backend entity for
 * why `whyOpen` is the field the model exists for.
 */
export type EpisodeState = 'open' | 'pending_follow_up' | 'closed';

export interface Episode {
  id: string;
  patientId: string;
  name: string;
  icdCode: string | null;
  state: EpisodeState;
  /** Why it is still open — null when nothing is being waited on. */
  whyOpen: string | null;
  openedAt: string;
  closedAt: string | null;
  closingNote: string | null;
  openedBy: string | null;
  /** How many visits sit on this thread, from one grouped query server-side. */
  visitCount: number;
  lastVisitAt: string | null;
}

export interface CreateEpisodeDto {
  patientId: string;
  name: string;
  icdCode?: string;
  whyOpen?: string;
  openedAt?: string;
  medicalRecordId?: string;
}

const unwrap = <T,>(body: { data?: T } | T): T =>
  (body as { data?: T })?.data ?? (body as T);

export const episodesApi = {
  byPatient: async (patientId: string): Promise<Episode[]> => {
    const r = await apiClient.get(`/episodes/patient/${patientId}`);
    return unwrap<Episode[]>(r.data) ?? [];
  },

  list: async (params?: { patientId?: string; state?: EpisodeState }): Promise<Episode[]> => {
    const r = await apiClient.get('/episodes', { params });
    return unwrap<Episode[]>(r.data) ?? [];
  },

  create: async (dto: CreateEpisodeDto): Promise<Episode> => {
    const r = await apiClient.post('/episodes', dto);
    return unwrap<Episode>(r.data);
  },

  update: async (
    id: string,
    dto: { name?: string; icdCode?: string; state?: 'open' | 'pending_follow_up'; whyOpen?: string },
  ): Promise<Episode> => {
    const r = await apiClient.patch(`/episodes/${id}`, dto);
    return unwrap<Episode>(r.data);
  },

  /** Closing stamps the date and clears `whyOpen` — a closed thread waits on nothing. */
  close: async (id: string, closingNote?: string): Promise<Episode> => {
    const r = await apiClient.post(`/episodes/${id}/close`, { closingNote });
    return unwrap<Episode>(r.data);
  },

  reopen: async (id: string): Promise<Episode> => {
    const r = await apiClient.post(`/episodes/${id}/reopen`, {});
    return unwrap<Episode>(r.data);
  },

  attach: async (id: string, medicalRecordId: string): Promise<Episode> => {
    const r = await apiClient.post(`/episodes/${id}/visits/${medicalRecordId}`, {});
    return unwrap<Episode>(r.data);
  },

  detach: async (medicalRecordId: string): Promise<void> => {
    await apiClient.delete(`/episodes/visits/${medicalRecordId}`);
  },
};
